import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';

const SecteurContext = createContext(null);

export const SecteurProvider = ({ children }) => {
  const [secteurs, setSecteurs] = useState([]);
  const [selectedSecteur, setSelectedSecteur] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allSecteurs, setAllSecteurs] = useState([]);
  const { user, roles } = useAuth();

  const processSectorsData = useCallback(async (sectorsData) => {
    try {
      const { data: mandateCountsResult, error: mandateCountError } = await supabase
        .rpc('count_mandates_by_sector');

      if (mandateCountError) throw mandateCountError;

      const mandateCounts = mandateCountsResult.reduce((acc, item) => {
        acc[item.sector_id] = item.mandate_count;
        return acc;
      }, {});

      const sectorsWithMandateCount = sectorsData.map(sector => ({
        ...sector,
        mandate_count: mandateCounts[sector.id] || 0
      }));

      setAllSecteurs(sectorsWithMandateCount);
      const activeSecteurs = sectorsWithMandateCount.filter(sector => sector.is_active);
      setSecteurs(activeSecteurs);

      if (activeSecteurs.length > 0 && !selectedSecteur) {
        setSelectedSecteur(activeSecteurs[0].id);
      }
    } catch (error) {
      console.error('Erreur lors du traitement des données des secteurs:', error);
      throw error;
    }
  }, [selectedSecteur]);

  const fetchSecteurs = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!roles?.can_view_all_sectors && user?.id) {
        
        // Récupérer uniquement les secteurs associés à l'utilisateur
        const { data: userSectors, error: userSectorsError } = await supabase
          .from('user_sectors')
          .select('sector_id')
          .eq('user_id', user.id);

        if (userSectorsError) throw userSectorsError;
        

        if (!userSectors || userSectors.length === 0) {
        
          setAllSecteurs([]);
          setSecteurs([]);
          setLoading(false);
          return;
        }

        const sectorIds = userSectors.map(us => us.sector_id);
        // Utiliser la bonne syntaxe pour la requête Supabase
        const { data, error } = await supabase
          .from('sectors')
          .select('*')
          .filter('id', 'in', `(${sectorIds.join(',')})`)
          .order('name');

        if (error) throw error;
        processSectorsData(data);
      } else {
        // Si l'utilisateur peut voir tous les secteurs
        const { data, error } = await supabase
          .from('sectors')
          .select('*')
          .order('name');

        if (error) throw error;
        processSectorsData(data);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des secteurs:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }, [roles, user, processSectorsData]);

  useEffect(() => {
    if (user) {
      fetchSecteurs();
    }
  }, [user, roles, fetchSecteurs]);

  return (
    <SecteurContext.Provider value={{ 
      secteurs, 
      selectedSecteur, 
      setSelectedSecteur: (sectorId) => {
        // Vérifier que le secteur sélectionné est actif
        const sector = secteurs.find(s => s.id === sectorId);
        if (sector) {
          setSelectedSecteur(sectorId);
        }
      },
      refreshSecteurs: fetchSecteurs,
      loading,
      error,
      // Conserver tous les secteurs pour la gestion
      allSecteurs
    }}>
      {children}
    </SecteurContext.Provider>
  );
};

export const useSecteur = () => {
  const context = useContext(SecteurContext);
  if (!context) {
    throw new Error('useSecteur must be used within a SecteurProvider');
  }
  return context;
};
